import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { API_URL } from "../../utils/config";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { formatNumero, formatAddress, formatDate } from "../../utils/Utilitats";
import DetallsOferta from "./Detallsoferta";
import DetallsProducteOferta from "./Detallsproducteoferta";
import AjudaOfertes from "./AjudaOfertes";
import "../dashboard_v2.css";

// Component compartit d'Ofertes: el mateix UI serveix per Pressupostos i Treballs.
// tipus: 'pressupost' | 'treball'
// endpoints: Ofertespressupost.php / Ofertestreballs.php
// Treballs i Pressupostos passen `subnav`. No duplicar aquesta lògica a Ofertespressupost.js.

function idOferta(o) {
  return o ? Number(o.idOfertes) : null;
}

function idProducte(p) {
  return p ? Number(p.idProductesOfertes) : null;
}

function classeNiu(nivell) {
  return `dv2-nested--${((Number(nivell) - 1) % 4) + 1}`;
}

function ordenarFiles(rows, { key, direction }, numerics = []) {
  if (!key) return rows;
  const dir = direction === "asc" ? 1 : -1;
  const nums = new Set(numerics);
  return [...rows].sort((a, b) => {
    if (nums.has(key)) {
      return ((Number(a[key]) || 0) - (Number(b[key]) || 0)) * dir;
    }
    const va = (a[key] ?? "").toString();
    const vb = (b[key] ?? "").toString();
    return va.localeCompare(vb, "ca", { numeric: true, sensitivity: "base" }) * dir;
  });
}

function ordenarOfertes(rows, cfg) {
  return ordenarFiles(rows, cfg, ["PreuTotal"]);
}

function ordenarProductes(rows, cfg) {
  return ordenarFiles(rows, cfg, ["Quantitat", "PreuUnitari"]);
}

function Ofertes({ tipus, entitat, onCancel, subnav }) {
  const { rolUsuari } = useUserContext();
  const potCrear = tincPermis(rolUsuari, "CREAR");
  const potConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const potModificar = tincPermis(rolUsuari, "MODIFICAR");
  const potEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const [activeTab, setActiveTab] = useState("table");
  const [ofertesList, setOfertesList] = useState([]);
  const [selectedOferta, setSelectedOferta] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  const [selectedProducte, setSelectedProducte] = useState(null);
  const [productesByOferta, setProductesByOferta] = useState({});
  const [carregantProductes, setCarregantProductes] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "DataOferta", direction: "desc" });
  const [sortProductes, setSortProductes] = useState({});
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const esFitxa = activeTab !== "table";
  const esProducte = activeTab.endsWith("Producte");

  const handleEsc = useCallback(() => {
    if (activeTab === "table") onCancel?.();
    else {
      setActiveTab("table");
      setMenuAccions(false);
    }
  }, [activeTab, onCancel]);
  TeclaEscEnter(handleEsc, null);

  const fetchOfertes = useCallback(async () => {
    const entitatId = tipus === "pressupost" ? entitat?.idPressupostos : entitat?.idTreballs;
    if (!entitatId) return [];
    try {
      const endpoint = tipus === "pressupost" ? "Ofertespressupost.php" : "Ofertestreballs.php";
      const paramName = tipus === "pressupost" ? "idPressupostos" : "idTreballs";
      const response = await fetch(`${API_URL}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [paramName]: entitatId }),
      });
      const data = await response.json();
      const llista = data.success ? data.ofertes || [] : [];
      setOfertesList(llista);
      return llista;
    } catch (error) {
      console.error("Error carregant ofertes:", error);
      setOfertesList([]);
      return [];
    }
  }, [entitat, tipus]);

  const fetchProductes = useCallback(async (id) => {
    if (!id) return [];
    setCarregantProductes((p) => ({ ...p, [id]: true }));
    try {
      const response = await fetch(`${API_URL}/Productesoferta.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idOfertes: id }),
      });
      const data = await response.json();
      const llista = data.success ? data.productes || [] : [];
      setProductesByOferta((p) => ({ ...p, [id]: llista }));
      return llista;
    } catch (error) {
      console.error("Error carregant productes:", error);
      setProductesByOferta((p) => ({ ...p, [id]: [] }));
      return [];
    } finally {
      setCarregantProductes((p) => ({ ...p, [id]: false }));
    }
  }, []);

  useEffect(() => {
    fetchOfertes();
  }, [fetchOfertes]);

  useEffect(() => {
    setSelectedOferta((prev) => {
      if (!prev) return prev;
      return ofertesList.find((o) => idOferta(o) === idOferta(prev)) || prev;
    });
  }, [ofertesList]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  useEffect(() => {
    if (!activeTab.startsWith("consultar")) setMenuAccions(false);
  }, [activeTab]);

  const tornarTaula = () => {
    setActiveTab("table");
    setMenuAccions(false);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const ofertesOrdenades = useMemo(
    () => ordenarOfertes(ofertesList, sortConfig),
    [ofertesList, sortConfig]
  );

  const handleSortProductes = (id, key) => {
    setSortProductes((prev) => {
      const curr = prev[id] || { key: null, direction: "asc" };
      return {
        ...prev,
        [id]: {
          key,
          direction: curr.key === key && curr.direction === "asc" ? "desc" : "asc",
        },
      };
    });
  };

  const productesOrdenatsDe = (id) =>
    ordenarProductes(productesByOferta[id] || [], sortProductes[id] || { key: null, direction: "asc" });

  const th = (key, label, extra = "") => (
    <th
      className={`${extra} ${sortConfig.key === key ? "is-sorted" : ""}`.trim()}
      onClick={() => handleSort(key)}
    >
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      fetchProductes(id);
      return [...prev, id];
    });
  };

  const obrirMenu = (e, clau) => {
    e.stopPropagation();
    if (menuObert === clau) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 160;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(clau);
  };

  const obrirOferta = (oferta, tab) => {
    setSelectedOferta(oferta);
    setSelectedProducte(null);
    setMenuObert(null);
    setActiveTab(tab);
  };

  const obrirProducte = (oferta, producte, tab) => {
    setSelectedOferta(oferta);
    setSelectedProducte(producte);
    setMenuObert(null);
    setActiveTab(tab);
  };

  const despresOferta = async (creada) => {
    const llista = await fetchOfertes();
    const id = Number(creada?.idOfertes);
    if (id) {
      const actual = llista.find((o) => idOferta(o) === id) || creada;
      setSelectedOferta(actual);
      setExpandedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      fetchProductes(id);
    }
    tornarTaula();
  };

  const despresProducte = async () => {
    const id = idOferta(selectedOferta);
    await fetchOfertes();
    if (id) await fetchProductes(id);
    setSelectedProducte(null);
    tornarTaula();
  };

  const renderAccionsOferta = (oferta) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === `oferta-${idOferta(oferta)}` ? "is-open" : ""}`}
        aria-label="Accions de l'oferta"
        onClick={(e) => obrirMenu(e, `oferta-${idOferta(oferta)}`)}
      >
        …
      </button>
    </div>
  );

  const renderAccionsProducte = (oferta, prod) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === `prod-${idProducte(prod)}` ? "is-open" : ""}`}
        aria-label="Accions del producte"
        onClick={(e) => {
          setSelectedOferta(oferta);
          obrirMenu(e, `prod-${idProducte(prod)}`);
        }}
      >
        …
      </button>
    </div>
  );

  const renderSubtaula = (oferta) => {
    const id = idOferta(oferta);
    const productes = productesOrdenatsDe(id);
    const carregant = !!carregantProductes[id];
    const sortP = sortProductes[id] || { key: null, direction: "asc" };
    const thProd = (key, label, extra = "") => (
      <th
        className={`${extra} ${sortP.key === key ? "is-sorted" : ""}`.trim()}
        onClick={(e) => {
          e.stopPropagation();
          handleSortProductes(id, key);
        }}
      >
        {label} {sortP.key === key ? (sortP.direction === "asc" ? "↑" : "↓") : ""}
      </th>
    );
    return (
      <div className={`dv2-nested ${classeNiu(1)}`}>
        <div className="dv2-nested-head">
          <span>Productes de l'oferta{productes.length ? ` · ${productes.length}` : ""}</span>
          {potCrear && (
            <button
              type="button"
              className="dv2-btn dv2-btn--compact dv2-btn--primary"
              onClick={() => obrirProducte(oferta, null, "nouProducte")}
            >
              Nou producte
            </button>
          )}
        </div>
        {carregant ? (
          <p className="dv2-empty">Carregant productes…</p>
        ) : productes.length === 0 ? (
          <p className="dv2-empty">Aquesta oferta encara no té productes.</p>
        ) : (
          <table className="dv2-table">
            <thead>
              <tr>
                {thProd("Quantitat", "Quantitat", "dv2-num")}
                {thProd("UnitatMesura", "Unitat")}
                {thProd("Marca", "Marca")}
                {thProd("Model", "Model")}
                {thProd("PreuUnitari", "Preu unitari", "dv2-num")}
                {thProd("Descripcio", "Descripció")}
                <th className="dv2-table-actions">Accions</th>
              </tr>
            </thead>
            <tbody>
              {productes.map((prod) => {
                const isSelected = idProducte(selectedProducte) === idProducte(prod);
                return (
                  <tr
                    key={idProducte(prod)}
                    className={isSelected ? "is-selected" : ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOferta(oferta);
                      setSelectedProducte(isSelected ? null : prod);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (potConsultar) obrirProducte(oferta, prod, "consultarProducte");
                    }}
                  >
                    <td className="dv2-num">{prod.Quantitat}</td>
                    <td>{prod.UnitatMesura || "—"}</td>
                    <td>{prod.Marca || "—"}</td>
                    <td>{prod.Model || "—"}</td>
                    <td className="dv2-num">{formatNumero(prod.PreuUnitari)}</td>
                    <td className="dv2-cell-clip" title={prod.Descripcio || ""}>
                      {prod.Descripcio || "—"}
                    </td>
                    <td className="dv2-table-actions">{renderAccionsProducte(oferta, prod)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderProductesMobil = (oferta) => {
    const id = idOferta(oferta);
    const productes = productesOrdenatsDe(id);
    const carregant = !!carregantProductes[id];
    return (
      <div className={`dv2-record-children ${classeNiu(1)}`}>
        {carregant ? (
          <p className="dv2-empty">Carregant productes…</p>
        ) : productes.length === 0 ? (
          <p className="dv2-empty">Aquesta oferta encara no té productes.</p>
        ) : (
          productes.map((prod) => {
            const isSelected = idProducte(selectedProducte) === idProducte(prod);
            return (
              <article
                key={idProducte(prod)}
                className={`dv2-record ${isSelected ? "is-selected" : ""}`}
                onClick={() => {
                  setSelectedOferta(oferta);
                  setSelectedProducte(isSelected ? null : prod);
                }}
                onDoubleClick={() => potConsultar && obrirProducte(oferta, prod, "consultarProducte")}
              >
                <div>
                  <p className="dv2-record-title">
                    {prod.Quantitat} {prod.UnitatMesura} · {prod.Marca} {prod.Model}
                  </p>
                  <p className="dv2-record-meta">
                    {formatNumero(prod.PreuUnitari)} €
                    {prod.Descripcio ? ` · ${prod.Descripcio}` : ""}
                  </p>
                </div>
                {renderAccionsProducte(oferta, prod)}
              </article>
            );
          })
        )}
        {potCrear && (
          <button
            type="button"
            className="dv2-btn dv2-btn--compact dv2-btn--primary"
            onClick={() => obrirProducte(oferta, null, "nouProducte")}
          >
            Nou producte
          </button>
        )}
      </div>
    );
  };

  const ofertaMenu = menuObert?.startsWith("oferta-")
    ? ofertesList.find((o) => `oferta-${idOferta(o)}` === menuObert)
    : null;
  const producteMenu = (() => {
    if (!menuObert?.startsWith("prod-")) return null;
    for (const oferta of ofertesList) {
      const llista = productesByOferta[idOferta(oferta)] || [];
      const prod = llista.find((p) => `prod-${idProducte(p)}` === menuObert);
      if (prod) return { oferta, prod };
    }
    return null;
  })();

  const renderTaula = () => {
    if (ofertesList.length === 0) {
      return (
        <p className="dv2-empty">
          No hi ha ofertes per aquest {tipus === "pressupost" ? "pressupost" : "treball"}.
        </p>
      );
    }

    return (
      <section className="dv2-card">
        {isNarrow ? (
          <div className="dv2-recordList" style={{ display: "flex" }}>
            {ofertesOrdenades.map((oferta) => {
              const id = idOferta(oferta);
              const isSelected = idOferta(selectedOferta) === id;
              const isExpanded = expandedIds.includes(id);
              return (
                <React.Fragment key={id}>
                  <article
                    className={`dv2-record ${isSelected ? "is-selected" : ""} ${isExpanded ? "is-open" : ""}`}
                    onClick={() => setSelectedOferta(isSelected ? null : oferta)}
                    onDoubleClick={() => potConsultar && obrirOferta(oferta, "consultar")}
                  >
                    <div>
                      <p className="dv2-record-title">{oferta.NomProveidor || "Sense proveïdor"}</p>
                      <p className="dv2-record-meta">
                        {[
                          formatDate(oferta.DataOferta),
                          oferta.ReferenciaProv,
                          oferta.Descripcio,
                          oferta.PreuTotal != null ? `${formatNumero(oferta.PreuTotal)} €` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <button
                        type="button"
                        className={`dv2-expandBtn ${isExpanded ? "is-open" : ""}`}
                        aria-label={isExpanded ? "Plegar productes" : "Veure productes"}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(id);
                        }}
                      >
                        {isExpanded ? "▼" : "▶"}
                      </button>
                      {renderAccionsOferta(oferta)}
                    </div>
                  </article>
                  {isExpanded && renderProductesMobil(oferta)}
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className="dv2-tableWrap">
            <table className="dv2-table">
              <thead>
                <tr>
                  <th className="dv2-col-expand"></th>
                  {th("DataOferta", "Data")}
                  {th("NomProveidor", "Proveïdor")}
                  {th("ReferenciaProv", "Referència")}
                  {th("Descripcio", "Descripció")}
                  {th("PreuTotal", "Preu total", "dv2-num")}
                  {th("DataFiOferta", "Fi de validesa")}
                  <th className="dv2-table-actions">Accions</th>
                </tr>
              </thead>
              <tbody>
                {ofertesOrdenades.map((oferta) => {
                  const id = idOferta(oferta);
                  const isSelected = idOferta(selectedOferta) === id;
                  const isExpanded = expandedIds.includes(id);
                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`${isSelected ? "is-selected" : ""} ${isExpanded ? "is-open" : ""}`}
                        onClick={() => setSelectedOferta(isSelected ? null : oferta)}
                        onDoubleClick={() => potConsultar && obrirOferta(oferta, "consultar")}
                      >
                        <td className="dv2-col-expand">
                          <button
                            type="button"
                            className={`dv2-expandBtn ${isExpanded ? "is-open" : ""}`}
                            aria-label={isExpanded ? "Plegar productes" : "Veure productes"}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(id);
                            }}
                          >
                            {isExpanded ? "▼" : "▶"}
                          </button>
                        </td>
                        <td>{formatDate(oferta.DataOferta) || "—"}</td>
                        <td>{oferta.NomProveidor || "—"}</td>
                        <td>{oferta.ReferenciaProv || "—"}</td>
                        <td className="dv2-cell-clip" title={oferta.Descripcio || ""}>
                          {oferta.Descripcio || "—"}
                        </td>
                        <td className="dv2-num">{formatNumero(oferta.PreuTotal)}</td>
                        <td>{formatDate(oferta.DataFiOferta) || "—"}</td>
                        <td className="dv2-table-actions">{renderAccionsOferta(oferta)}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="is-nested">
                          <td colSpan={8}>{renderSubtaula(oferta)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {(ofertaMenu || producteMenu) &&
          menuPos &&
          createPortal(
            <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
              {ofertaMenu && (
                <>
                  {potConsultar && (
                    <button type="button" className="dv2-menu-item" onClick={() => obrirOferta(ofertaMenu, "consultar")}>
                      Consultar
                    </button>
                  )}
                  {potModificar && (
                    <button type="button" className="dv2-menu-item" onClick={() => obrirOferta(ofertaMenu, "modificar")}>
                      Modificar
                    </button>
                  )}
                  {potEsborrar && (
                    <button
                      type="button"
                      className="dv2-menu-item is-danger"
                      onClick={() => obrirOferta(ofertaMenu, "esborrar")}
                    >
                      Esborrar
                    </button>
                  )}
                </>
              )}
              {producteMenu && (
                <>
                  {potConsultar && (
                    <button
                      type="button"
                      className="dv2-menu-item"
                      onClick={() => obrirProducte(producteMenu.oferta, producteMenu.prod, "consultarProducte")}
                    >
                      Consultar
                    </button>
                  )}
                  {potModificar && (
                    <button
                      type="button"
                      className="dv2-menu-item"
                      onClick={() => obrirProducte(producteMenu.oferta, producteMenu.prod, "modificarProducte")}
                    >
                      Modificar
                    </button>
                  )}
                  {potEsborrar && (
                    <button
                      type="button"
                      className="dv2-menu-item is-danger"
                      onClick={() => obrirProducte(producteMenu.oferta, producteMenu.prod, "esborrarProducte")}
                    >
                      Esborrar
                    </button>
                  )}
                </>
              )}
            </div>,
            document.body
          )}
      </section>
    );
  };

  const renderFitxa = () => {
    switch (activeTab) {
      case "nou":
        return (
          <DetallsOferta
            mode="nou"
            tipus={tipus}
            entitat={entitat}
            onSave={despresOferta}
            onCancel={tornarTaula}
          />
        );
      case "consultar":
        return selectedOferta ? (
          <DetallsOferta
            mode="consultar"
            tipus={tipus}
            entitat={entitat}
            oferta={selectedOferta}
            ofertesList={ofertesOrdenades}
            onNavigate={setSelectedOferta}
            onCancel={tornarTaula}
          />
        ) : null;
      case "modificar":
        return selectedOferta ? (
          <DetallsOferta
            mode="modificar"
            tipus={tipus}
            entitat={entitat}
            oferta={selectedOferta}
            onSave={despresOferta}
            onCancel={tornarTaula}
          />
        ) : null;
      case "esborrar":
        return selectedOferta ? (
          <DetallsOferta
            mode="esborrar"
            tipus={tipus}
            entitat={entitat}
            oferta={selectedOferta}
            onSave={async () => {
              setSelectedOferta(null);
              await fetchOfertes();
              tornarTaula();
            }}
            onCancel={tornarTaula}
          />
        ) : null;
      case "nouProducte":
        return selectedOferta ? (
          <DetallsProducteOferta
            mode="nou"
            oferta={selectedOferta}
            producte={null}
            onSave={despresProducte}
            onCancel={tornarTaula}
          />
        ) : null;
      case "consultarProducte":
        return selectedProducte ? (
          <DetallsProducteOferta
            mode="consultar"
            oferta={selectedOferta}
            producte={selectedProducte}
            productesList={productesOrdenatsDe(idOferta(selectedOferta))}
            onNavigate={setSelectedProducte}
            onCancel={tornarTaula}
          />
        ) : null;
      case "modificarProducte":
        return selectedProducte ? (
          <DetallsProducteOferta
            mode="modificar"
            oferta={selectedOferta}
            producte={selectedProducte}
            onSave={despresProducte}
            onCancel={tornarTaula}
          />
        ) : null;
      case "esborrarProducte":
        return selectedProducte ? (
          <DetallsProducteOferta
            mode="esborrar"
            oferta={selectedOferta}
            producte={selectedProducte}
            onSave={despresProducte}
            onCancel={tornarTaula}
          />
        ) : null;
      default:
        return null;
    }
  };

  const titol =
    activeTab === "nou"
      ? "Nova oferta"
      : activeTab === "consultar"
      ? "Oferta"
      : activeTab === "modificar"
      ? "Modificar oferta"
      : activeTab === "esborrar"
      ? "Esborrar oferta"
      : activeTab === "nouProducte"
      ? "Nou producte d'oferta"
      : activeTab === "consultarProducte"
      ? "Producte d'oferta"
      : activeTab === "modificarProducte"
      ? "Modificar producte"
      : activeTab === "esborrarProducte"
      ? "Esborrar producte"
      : "Ofertes";

  const subtitol = [
    tipus === "pressupost"
      ? entitat?.idPresIntern || entitat?.idPressupostos
      : entitat?.idTreballIntern || entitat?.idTreballs,
    entitat?.NomClient,
    esProducte && selectedOferta?.NomProveidor,
  ]
    .filter(Boolean)
    .join(" · ");

  const adrecaTxt = !subnav
    ? formatAddress({
        TipusVia: entitat?.TipusVia,
        NomVia: entitat?.NomVia,
        NumVia: entitat?.NumVia,
        EscalaVia: entitat?.EscalaVia,
        Pis: entitat?.Pis,
        Porta: entitat?.Porta,
        Poblacio: entitat?.Poblacio,
        CodiPostal: entitat?.CodiPostal,
      })
    : "";

  const esEsborrar = activeTab === "esborrar" || activeTab === "esborrarProducte";

  return (
    <div className={subnav ? "dv2-entity" : "dv2 dv2-entity"}>
      {mostraAjuda && <AjudaOfertes tipus={tipus} onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${esEsborrar ? "is-danger" : ""}`} aria-hidden="true">
            {esEsborrar ? "🗑️" : "💶"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">
              {subtitol}
              {adrecaTxt && adrecaTxt !== "—" ? ` · ${adrecaTxt}` : ""}
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title="Com funcionen les Ofertes"
            aria-label="Ajuda"
            onClick={() => setMostraAjuda(true)}
          >
            ?
          </button>
          <button type="button" className="dv2-btn" onClick={esFitxa ? tornarTaula : onCancel}>
            Tornar
          </button>
          {activeTab === "table" && potCrear && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={() => {
                setSelectedOferta(null);
                setSelectedProducte(null);
                setActiveTab("nou");
              }}
            >
              Nova oferta
            </button>
          )}
          {activeTab === "consultar" && potModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => setActiveTab("modificar")}>
              Modificar
            </button>
          )}
          {activeTab === "consultarProducte" && potModificar && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={() => setActiveTab("modificarProducte")}
            >
              Modificar
            </button>
          )}
          {(activeTab === "consultar" || activeTab === "consultarProducte") && potEsborrar && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      setActiveTab(activeTab === "consultar" ? "esborrar" : "esborrarProducte");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {subnav}
      {activeTab === "table" ? <div className="dv2-entity-body">{renderTaula()}</div> : renderFitxa()}
    </div>
  );
}

export default Ofertes;
